import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, forkJoin } from 'rxjs';

import { RequestService } from '../request';
import { ICreateUpdateUserRequest, IUser } from './defs';
import { PaginatedResponse } from '../common/models/paginated-response.type';
import { SisifoService } from '@app/features/patient-requests/services/center.service';

export type SisifoCenter = {
  centroId: number;
  centroName: string;
  centroDefault?: boolean;
  /** Number of users assigned to this center (from list). */
  userCount?: number;
  services?: SisifoService[];
};

export type SisifoUser = {
  nombre: string;
  sesion: number;
  arztNr: string;
  login: string;
  eMail: string;
  apellidos: string;
  langId: string;
  fClave: string;
  pd: number;
  fRegistro: string;
  userID: number;
  activa: number;
  dias: number;
  levels: number;
  pwd: string;
  centers?: SisifoCenter[];
};

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private sisifoDefaultCenterId: number = null;

  constructor(private request: RequestService) {}

  public getList(
    limit: number = 10,
    offset: number = 0,
    sortField: string = 'username',
    sortOrder: string = 'asc',
    filters?: Record<string, any>
  ): Observable<PaginatedResponse<IUser>> {
    let filter = '';
    if (filters && Object.keys(filters).length > 0) {
      const filterParams = Object.entries(filters)
        .filter(([key, value]) => value && value.length > 0 && value[0]?.value)
        .map(([key, value]) => `${key}=${value[0]?.value}`)
        .join('&');
      filter = `&${filterParams}`;
    }

    return this.request.get<PaginatedResponse<IUser>>(
      `/api/users?limit=${limit}&offset=${offset}&sortField=${sortField}&sortOrder=${sortOrder}${filter}`
    );
  }

  public get(id: number): Observable<IUser> {
    return this.request.get(`/api/users/${id}`);
  }

  public checkUserNameAndEmail(userNameOrEmail: string): Observable<boolean> {
    return this.request.get<boolean>(
      `/api/users/check-user-name-and-email/${userNameOrEmail}`
    );
  }

  public getMyUser(): Observable<IUser> {
    return this.request.get('/api/users/my-user');
  }

  public getSisifoUserData(): Observable<SisifoUser> {
    return this.request.get('/api/users/sisifo/my-user');
  }

  public getSisifoUser(username): Observable<SisifoUser> {
    return this.request.get(`/api/users/sisifo/users/${username}`);
  }

  public getSisifoCenters(): Observable<SisifoCenter[]> {
    return this.request.get<SisifoCenter[]>('/api/users/sisifo/my-centers');
  }

  public getSisifoAllCenters(): Observable<SisifoCenter[]> {
    return this.request.get<SisifoCenter[]>('/api/users/sisifo/centers');
  }

  public setSisifoDefaultCenter(centerId: number): void {
    this.sisifoDefaultCenterId = centerId;
  }

  public getSisifoDefaultCenter(): number {
    return this.sisifoDefaultCenterId;
  }

  public createUser(request: ICreateUpdateUserRequest): Observable<IUser> {
    return this.request.post('/api/users', request);
  }

  public updateUser(request: ICreateUpdateUserRequest): Observable<IUser> {
    delete request.confirmPassword; // Remove password confirmation if exists
    return this.request.put(`/api/users/${request.id}`, request);
  }

  public deleteUser(ids: number[]): Observable<IUser[]> {
    return forkJoin(ids.map((id) => this.request.delete(`/api/users/${id}`)));
  }

  public enableDisableUser(id: string, enable: boolean) {
    return firstValueFrom(
      this.request.put(`/api/users/${id}/${enable ? 'en' : 'dis'}able/`, '')
    );
  }
}
